import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import './ServicesPage.css';
import SEO from '../../components/SEO';
// Fallback pillar images
import CorporateAdvisoryImg from '../../Assets/Image/Corporate Advisory_0.webp';
import DigitalPlatformImg from '../../Assets/Image/Digital Platform.webp';
import InternationalFirmsImg from '../../Assets/Image/International and Regional Firms.webp';
// Fallback sub-service images
import CompaniesEstablishmentImg from '../../Assets/Image/Companies establishment_0.webp';
import CorporateRestructuringImg from '../../Assets/Image/Corporate change and Ownership structure_2.webp';
import ComplianceRegulatoryImg from '../../Assets/Image/Compliance and Regulatory_1.webp';
import EmploymentImg from '../../Assets/Image/Employment_6.webp';
import RealEstateImg from '../../Assets/Image/Real Estate_12.webp';
import PlatformsLicensingImg from '../../Assets/Image/Platforms Licensing and Compliance_11.webp';
import DataPrivacyImg from '../../Assets/Image/Data Protection & Privacy_3.webp';
import FintechImg from '../../Assets/Image/Fintech_7.webp';
import IntellectualPropertyImg from '../../Assets/Image/Intellectual Property _10.webp';
import DraftingReviewImg from '../../Assets/Image/Drafting & Review_4.webp';
import DueDiligenceImg from '../../Assets/Image/Due Diligence_5.webp';
import TranslationImg from '../../Assets/Image/Translation_13.webp';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const pillarImageFallbacks = {
  'Corporate Advisory': CorporateAdvisoryImg,
  'Digital Platform': DigitalPlatformImg,
  'International and Regional Firms': InternationalFirmsImg,
};

const subserviceImageFallbacks = {
  'Companies Establishment': CompaniesEstablishmentImg,
  'Corporate Restructuring': CorporateRestructuringImg,
  'Compliance and Regulatory': ComplianceRegulatoryImg,
  'Employment': EmploymentImg,
  'Real Estate': RealEstateImg,
  'Platforms Licensing and Compliance': PlatformsLicensingImg,
  'Data Protection & Privacy': DataPrivacyImg,
  'Fintech': FintechImg,
  'Intellectual Property (IP)': IntellectualPropertyImg,
  'Drafting & Review': DraftingReviewImg,
  'Due Diligence': DueDiligenceImg,
  'Translation': TranslationImg,
};

const defaultPillars = [
  {
    id: 'corporate',
    name: 'Corporate Advisory',
    image: '',
    subservices: [
      { id: 'companies-establishment', label: 'Companies Establishment', description: '', image: '' },
      { id: 'corporate-restructuring', label: 'Corporate Restructuring', description: '', image: '' },
      { id: 'compliance-regulatory', label: 'Compliance and Regulatory', description: '', image: '' },
      { id: 'employment', label: 'Employment', description: '', image: '' },
      { id: 'real-estate', label: 'Real Estate', description: '', image: '' },
    ],
  },
  {
    id: 'digital',
    name: 'Digital Platform',
    image: '',
    subservices: [
      { id: 'platforms-licensing', label: 'Platforms Licensing and Compliance', description: '', image: '' },
      { id: 'data-privacy', label: 'Data Protection & Privacy', description: '', image: '' },
      { id: 'fintech', label: 'Fintech', description: '', image: '' },
      { id: 'intellectual-property', label: 'Intellectual Property (IP)', description: '', image: '' },
    ],
  },
  {
    id: 'international',
    name: 'International and Regional Firms',
    image: '',
    subservices: [
      { id: 'drafting-review', label: 'Drafting & Review', description: '', image: '' },
      { id: 'due-diligence', label: 'Due Diligence', description: '', image: '' },
      { id: 'translation', label: 'Translation', description: '', image: '' },
    ],
  },
];

function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE_URL}${url}`;
}

const ServicesPage = () => {
  const [heroTitle, setHeroTitle] = useState('Our Practice Areas');
  const [heroSubtitle, setHeroSubtitle] = useState(
    'A curated suite of corporate and commercial services designed for ambitious businesses, investors and founders operating in Egypt and beyond.'
  );
  const [pillars, setPillars] = useState(defaultPillars);

  const [activePillar, setActivePillar] = useState(defaultPillars[0]);
  const [activeSubservice, setActiveSubservice] = useState(
    defaultPillars[0].subservices[0]
  );

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/services/page`);
        if (res.data && res.data.heroTitle !== undefined) {
          setHeroTitle(res.data.heroTitle);
          setHeroSubtitle(res.data.heroSubtitle || '');
        }
        if (Array.isArray(res.data?.pillars) && res.data.pillars.length > 0) {
          const nextPillars = res.data.pillars;
          setPillars(nextPillars);
          setActivePillar(nextPillars[0]);
          setActiveSubservice(
            nextPillars[0].subservices && nextPillars[0].subservices[0]
              ? nextPillars[0].subservices[0]
              : null
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };
    fetchPage();
  }, []);

  const heroBackgroundImage = useMemo(() => {
    const apiUrl = activePillar && activePillar.image;
    if (apiUrl) return resolveImageUrl(apiUrl);
    return pillarImageFallbacks[activePillar?.name] || null;
  }, [activePillar?.name, activePillar?.image]);

  const subserviceImage = useMemo(() => {
    if (!activeSubservice) return null;
    const apiUrl = activeSubservice.image;
    if (apiUrl) return resolveImageUrl(apiUrl);
    return subserviceImageFallbacks[activeSubservice.label] || null;
  }, [activeSubservice?.label, activeSubservice?.image]);

  const handlePillarChange = (pillar) => {
    setActivePillar(pillar);
    const first = pillar.subservices && pillar.subservices[0];
    setActiveSubservice(first || null);
  };

  const handleSubserviceClick = (subservice) => {
    setActiveSubservice(subservice);
  };

  return (
    <div className="services-page">
      <SEO
        title="Our Practice Areas"
        description="Explore Whales Advisory practice areas across corporate advisory, digital platforms, and cross-border legal support for international and regional firms."
        canonicalPath="/services"
        type="website"
      />
      {/* Hero with dynamic full-bleed background */}
      <section className="services-hero">
        <AnimatePresence mode="wait">
          {heroBackgroundImage && (
            <motion.div
              key={activePillar.id}
              className="services-hero-bg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                backgroundImage: `url("${heroBackgroundImage}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
          )}
        </AnimatePresence>
        <div className="container">
          <motion.div
            className="services-hero-inner"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <h1 className="services-hero-title">{heroTitle}</h1>
            <p className="services-hero-subtitle">{heroSubtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Interactive Practice Areas */}
      <motion.section
        className="practice-section"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      >
        <div className="container">
          <div className="practice-layout">
            {/* Left: Sub-service Image Switcher */}
            <div className="practice-image-pane">
              <AnimatePresence mode="wait">
                {subserviceImage && activeSubservice && (
                  <motion.img
                    key={activeSubservice.id}
                    src={subserviceImage}
                    alt={activeSubservice.label}
                    className="practice-image"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                  />
                )}
              </AnimatePresence>
            </div>

            {/* Right: Pillars & sub-services */}
            <div className="practice-content" id={`pillar-${activePillar.id}`}>
              <p className="practice-tag">PRACTICE AREAS</p>
              <h2 className="practice-title">{activePillar.name}</h2>

              <div className="practice-pillars">
                {pillars.map((pillar) => (
                  <button
                    key={pillar.id}
                    type="button"
                    className={`practice-pillar-chip ${
                      pillar.id === activePillar.id ? 'active' : ''
                    }`}
                    onClick={() => handlePillarChange(pillar)}
                  >
                    {pillar.name}
                  </button>
                ))}
              </div>

              <div className="practice-subservices">
                {(activePillar.subservices || []).map((sub) => (
                  <button
                    key={sub.id}
                    type="button"
                    className={`subservice-item ${
                      activeSubservice && sub.id === activeSubservice.id ? 'active' : ''
                    }`}
                    onClick={() => handleSubserviceClick(sub)}
                  >
                    <span className="subservice-label">{sub.label}</span>
                    {activeSubservice && sub.id === activeSubservice.id && (
                      <span className="subservice-arrow">→</span>
                    )}
                  </button>
                ))}
              </div>

              <div className="practice-detail-pill">
                <div className="practice-detail-text">
                  <h3 className="practice-detail-title">
                    {activeSubservice ? activeSubservice.label : ''}
                  </h3>
                  <p className="practice-detail-body">
                    {activeSubservice ? activeSubservice.description : ''}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default ServicesPage;
